import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api/client';
import type { Category, MonthSummary, TransactionPage } from '../api/types';
import { formatEuro, percent } from '../lib/format';
import { usePeriod } from '../lib/period';
import { MonthSwitcher } from '../components/MonthSwitcher';
import { TransactionTimeline } from '../components/TransactionRow';
import { Bar, Card, Dot, EmptyState, SectionTitle, Spinner, Tile } from '../components/ui';

/**
 * Vista mensile. E' il blocco J:L del foglio (categoria, totale, percentuale)
 * riscritto come elenco ordinato leggibile su telefono, piu' tutti i movimenti
 * del mese con ricerca e filtro.
 */
export function MesePage() {
  const [period, setPeriod] = usePeriod();
  const [query, setQuery] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [limit, setLimit] = useState(50);

  const { data: summary, isLoading } = useQuery({
    queryKey: ['summary', 'month', period.year, period.month],
    queryFn: () =>
      api.get<MonthSummary>(`/summary/month?year=${period.year}&month=${period.month}`),
  });

  const { data: categories } = useQuery({
    queryKey: ['categories'],
    queryFn: () => api.get<Category[]>('/categories'),
  });

  const { data: page } = useQuery({
    queryKey: ['transactions', period.year, period.month, query, categoryId, limit],
    queryFn: () => {
      const params = new URLSearchParams({
        year: String(period.year),
        month: String(period.month),
        limit: String(limit),
      });
      if (query.trim()) params.set('q', query.trim());
      if (categoryId) params.set('categoryId', categoryId);
      return api.get<TransactionPage>(`/transactions?${params.toString()}`);
    },
  });

  return (
    <div>
      <MonthSwitcher period={period} onChange={setPeriod} />

      {isLoading || !summary ? (
        <Spinner />
      ) : (
        <div className="flex flex-col gap-5">
          <div className="grid grid-cols-3 gap-2">
            <Tile label="Spese" value={formatEuro(summary.expenses)} />
            <Tile label="Guadagno" value={formatEuro(summary.income)} />
            <Tile
              label="Risparmio"
              value={formatEuro(summary.savings)}
              tone={summary.savings >= 0 ? 'positive' : 'negative'}
            />
          </div>

          <section>
            <SectionTitle>Dove sono finiti i soldi</SectionTitle>
            <Card className="flex flex-col gap-3">
              {summary.categories.length === 0 ? (
                <EmptyState title="Nessuna spesa in questo mese" />
              ) : (
                summary.categories.map((c) => (
                  <button
                    key={c.categoryId}
                    type="button"
                    onClick={() =>
                      setCategoryId((current) => (current === c.categoryId ? '' : c.categoryId))
                    }
                    className="text-left"
                  >
                    <div className="mb-1 flex items-baseline gap-2">
                      <Dot color={c.color} />
                      <span
                        className={`flex-1 truncate text-sm ${
                          categoryId === c.categoryId ? 'font-semibold text-accent' : ''
                        }`}
                      >
                        {c.name}
                      </span>
                      <span className="tnum text-sm font-medium">{formatEuro(c.amount)}</span>
                      <span className="tnum w-12 text-right text-xs text-muted">
                        {percent(c.share)}
                      </span>
                    </div>
                    <Bar ratio={c.share} color={c.color} />
                  </button>
                ))
              )}
            </Card>
          </section>

          <section>
            <SectionTitle
              action={
                categoryId ? (
                  <button
                    type="button"
                    onClick={() => setCategoryId('')}
                    className="text-xs text-accent"
                  >
                    Rimuovi filtro
                  </button>
                ) : undefined
              }
            >
              Movimenti {page ? `(${page.total})` : ''}
            </SectionTitle>

            <div className="mb-3 flex flex-col gap-2 sm:flex-row">
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Cerca per nota o categoria"
                className="w-full rounded-[var(--radius-control)] bg-surface px-3 py-2.5 text-sm"
              />
              <select
                value={categoryId}
                onChange={(e) => setCategoryId(e.target.value)}
                aria-label="Filtra per categoria"
                className="rounded-[var(--radius-control)] bg-surface px-2 py-2.5 text-sm sm:w-40"
              >
                <option value="">Tutte le categorie</option>
                {categories?.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>

            {page && page.items.length > 0 ? (
              <>
                <TransactionTimeline transactions={page.items} />
                {page.hasMore && (
                  <button
                    type="button"
                    onClick={() => setLimit((l) => l + 50)}
                    className="mt-4 w-full rounded-[var(--radius-control)] bg-surface py-2.5 text-sm text-muted"
                  >
                    Mostra altri
                  </button>
                )}
              </>
            ) : (
              <Card>
                <EmptyState title="Nessun movimento con questi filtri" />
              </Card>
            )}
          </section>
        </div>
      )}
    </div>
  );
}
